import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Toast from '../components/Toast';
import './Admin.css';

export default function Admin() {
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toast,        setToast]        = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data.users || []);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate('/today', { replace: true });
      } else {
        showToast('Error al cargar los usuarios');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/today', { replace: true });
    } else {
      load();
    }
  }, [user, navigate, load]);

  async function toggleActive(u) {
    try {
      const res = await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      setUsers(prev => prev.map(x => x.id === u.id ? res.data.user : x));
      showToast(res.data.user.is_active ? `${u.username} activado` : `${u.username} desactivado`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al actualizar el usuario');
    }
  }

  async function toggleAdmin(u) {
    try {
      const res = await api.put(`/admin/users/${u.id}`, { is_admin: !u.is_admin });
      setUsers(prev => prev.map(x => x.id === u.id ? res.data.user : x));
      showToast(res.data.user.is_admin ? `${u.username} es ahora administrador` : `${u.username} ya no es administrador`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al actualizar el usuario');
    }
  }

  async function confirmAndDelete(u) {
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      showToast(`Usuario ${u.username} eliminado`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al eliminar el usuario');
    } finally {
      setConfirmDelete(null);
    }
  }

  async function handleImpersonate(u) {
    if (u.id === user?.id) return;
    setImpersonatingId(u.id);
    try {
      const res = await api.post(`/admin/impersonate/${u.id}`);
      const { access_token, user: targetUser } = res.data;
      impersonate(access_token, targetUser);
      navigate('/routines', { replace: true });
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al impersonar usuario');
      setImpersonatingId(null);
    }
  }

  const activeCount = users.filter(u => u.is_active).length;
  const pendingCount = users.filter(u => !u.is_active).length;
  const adminCount = users.filter(u => u.is_admin).length;

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      <div className="page">
        <div className="page-header">
          <div className="page-title">Admin</div>
          <span className="text-muted text-sm">{users.length} usuarios</span>
        </div>

        <div className="admin-tabs px-16">
          <button
            type="button"
            className="admin-tab active"
            onClick={() => {}}
          >
            Usuarios
          </button>
          <button
            type="button"
            className="admin-tab"
            onClick={() => navigate('/admin/stats')}
          >
            Estadísticas
          </button>
          <button
            type="button"
            className="admin-tab"
            onClick={() => navigate('/admin/templates')}
          >
            Plantillas
          </button>
          <button
            type="button"
            className="admin-tab"
            onClick={() => navigate('/admin/options')}
          >
            Opciones
          </button>
        </div>

        {!loading && (
          <div className="stat-cards" style={{ marginTop: 0 }}>
            <div className="stat-card">
              <div className="stat-value">{users.length}</div>
              <div className="stat-label">Total</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#4caf50' }}>{activeCount}</div>
              <div className="stat-label">Activos</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#ff9800' }}>{pendingCount}</div>
              <div className="stat-label">Pendientes</div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : users.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👤</div>
            <div className="empty-state-title">No hay usuarios registrados</div>
          </div>
        ) : (
          <div className="admin-user-list px-16">
            {users.map(u => (
              <div key={u.id} className="admin-user-card card mb-8">
                <div className="admin-user-info">
                  <div className="admin-user-avatar">
                    {(u.username || 'U')[0].toUpperCase()}
                  </div>
                  <div className="admin-user-details">
                    <div className="admin-user-name">
                      {u.username}
                      {u.is_admin && <span className="admin-badge">Admin</span>}
                    </div>
                    <div className="admin-user-email text-muted text-xs">{u.email || '—'}</div>
                    <div className="admin-user-status">
                      <span className={`status-dot ${u.is_active ? 'status-dot--active' : 'status-dot--inactive'}`} />
                      <span className="text-xs text-muted">{u.is_active ? 'Activo' : 'Pendiente'}</span>
                    </div>
                  </div>
                </div>

                {confirmDelete === u.id ? (
                  <div className="admin-confirm-delete">
                    <span className="text-sm">¿Eliminar a <b>{u.username}</b>? Esta acción es irreversible.</span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" className="btn btn-xs btn-secondary" onClick={() => setConfirmDelete(null)}>Cancelar</button>
                      <button type="button" className="btn btn-xs btn-danger" onClick={() => confirmAndDelete(u)}>Confirmar</button>
                    </div>
                  </div>
                ) : (
                  <div className="admin-user-actions">
                    <button
                      type="button"
                      className={`btn btn-xs ${u.is_active ? 'btn-secondary' : 'btn-success'}`}
                      onClick={() => toggleActive(u)}
                    >
                      {u.is_active ? 'Desactivar' : 'Activar'}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-xs ${u.is_admin ? 'btn-secondary' : 'btn-primary'}`}
                      onClick={() => toggleAdmin(u)}
                      disabled={u.id === user?.id}
                    >
                      {u.is_admin ? 'Quitar admin' : 'Hacer admin'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-outline-danger"
                      onClick={() => setConfirmDelete(u.id)}
                      disabled={u.id === user?.id}
                    >
                      Eliminar
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs"
                      style={{ background: '#0f3460', color: '#fff' }}
                      onClick={() => handleImpersonate(u)}
                      disabled={u.id === user?.id || impersonatingId === u.id}
                    >
                      {impersonatingId === u.id ? '...' : 'Ser'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
